import { Component, OnInit, input, output, effect, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs';
import { toUtcDate } from 'src/app/shared/utils';
import { TranslatePipe } from '@next/core-lib/i18n';
import { BaseCropFieldRegistration } from '../disease-overview-table.component/models/models';
import { MatDatepicker, MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import {
  PlantDateWarningComponent,
} from '../plant-date-warning.component/plant-date-warning.component';
import { getPlantDateWarningCodes } from '../plant-date-warning.component/plant-date-warning.helpers';

export type DateFieldMode = 'editable' | 'disabled' | 'readonly-action';

export interface DateFieldConfig {
  labelKey: string;
  value?: Date | null;
  mode?: DateFieldMode;
  disabledReasonKey?: string;
}

type DateFieldName = 'emergence' | 'harvest';

@Component({
  selector: 'app-disease-insight-toolbar',
  standalone: true,
  templateUrl: './disease-insight-toolbar.component.html',
  styleUrl: './disease-insight-toolbar.component.scss',
  imports: [
    MatFormField,
    MatDatepicker,
    ReactiveFormsModule,
    TranslatePipe,
    MatTooltip,
    MatDatepickerModule,
    NgClass,
    MatLabel,
    MatInput,
    MatSuffix,
    MatDatepickerToggle,
    MatIcon,
    MatIconButton,
    PlantDateWarningComponent,
  ],
})
export class DiseaseInsightToolbarComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedCropField = input.required<BaseCropFieldRegistration>();
  readonly isParticipatingToBem = input(false);
  readonly showEmergenceDate = input(true);
  readonly showHarvestDate = input(true);
  readonly plantDateConfig = input<DateFieldConfig>({
    labelKey: 'shared.diseaseDetail.plantDate',
    mode: 'editable',
  });
  readonly emergenceDateConfig = input<DateFieldConfig>({
    labelKey: 'shared.diseaseDetail.emergenceDate',
    mode: 'editable',
  });
  readonly harvestDateConfig = input<DateFieldConfig>({
    labelKey: 'shared.diseaseDetail.harvestDate',
    mode: 'editable',
  });
  readonly plantDateMin = input<Date | undefined>(undefined);
  readonly plantDateMax = input<Date | undefined>(undefined);

  readonly plantDateChanged = output<Date>();
  readonly emergenceDateChanged = output<Date>();
  readonly harvestDateChanged = output<Date>();
  readonly plantDateChangedInFuture = output<boolean>();
  readonly readonlyDateActionTriggered = output<'emergence' | 'harvest'>();
  protected adviceDateMax: Date | undefined;
  protected adviceDateMin: Date | undefined;
  protected emergenceDateMin: Date | undefined;
  protected emergenceDateMax: Date | undefined;
  protected harvestDateMin: Date | undefined;
  protected harvestDateMax: Date | undefined;
  protected year: number | undefined;

  protected readonly plantingDateFormControl = new FormControl<Date | null>(null);
  protected readonly emergenceDateFormControl = new FormControl<Date | null>(null);
  protected readonly harvestDateFormControl = new FormControl<Date | null>(null);
  protected readonly plantDateWarningCodes = computed(() => {
    const field = this.selectedCropField();

    return getPlantDateWarningCodes({
      plantDate: field.plantDate,
      plantDateCreatedAt: field.plantDateCreatedAt,
      plantDateUpdatedAt: field.plantDateUpdatedAt,
      isParticipatingToBem: this.isParticipatingToBem(),
    });
  });
  protected readonly resolvedPlantDateConfig = computed(() => this.resolveDateFieldConfig(this.plantDateConfig(), 'shared.diseaseDetail.plantDate'));
  protected readonly resolvedEmergenceDateConfig = computed(() =>
    this.resolveDateFieldConfig(this.emergenceDateConfig(), 'shared.diseaseDetail.emergenceDate'),
  );
  protected readonly resolvedHarvestDateConfig = computed(() =>
    this.resolveDateFieldConfig(this.harvestDateConfig(), 'shared.diseaseDetail.harvestDate'),
  );
  protected readonly plantDateDisabledReasonKey = computed(() => {
    if (this.resolvedPlantDateConfig().mode === 'disabled') {
      return this.resolvedPlantDateConfig().disabledReasonKey ?? '';
    }

    const field = this.selectedCropField();
    if (field.isPlantDateConfirmed) {
      return 'UI.PlantDateConfirmedMessage';
    }
    if (this.isParticipatingToBem()) {
      return 'UI.PlantDateBemMessage';
    }

    return '';
  });
  protected readonly hasIntrinsicPlantDateRestriction = computed(
    () => this.selectedCropField().isPlantDateConfirmed || this.isParticipatingToBem(),
  );
  protected readonly isPlantDateInputDisabled = computed(
    () => this.resolvedPlantDateConfig().mode === 'disabled' || this.hasIntrinsicPlantDateRestriction(),
  );
  protected readonly isEmergenceDateReadonlyAction = computed(() => this.resolvedEmergenceDateConfig().mode === 'readonly-action');
  protected readonly isHarvestDateReadonlyAction = computed(() => this.resolvedHarvestDateConfig().mode === 'readonly-action');
  protected readonly isEmergenceDateDisabled = computed(() => this.resolvedEmergenceDateConfig().mode === 'disabled');
  protected readonly isHarvestDateDisabled = computed(() => this.resolvedHarvestDateConfig().mode === 'disabled');
  protected readonly emergenceDisabledTooltipText = computed(() =>
    this.isEmergenceDateDisabled() ? this.resolvedEmergenceDateConfig().disabledReasonKey ?? '' : '',
  );
  protected readonly harvestDisabledTooltipText = computed(() =>
    this.isHarvestDateDisabled() ? this.resolvedHarvestDateConfig().disabledReasonKey ?? '' : '',
  );
  protected readonly emergenceReadonlyActionReasonKey = computed(() =>
    this.isEmergenceDateReadonlyAction() ? this.resolvedEmergenceDateConfig().disabledReasonKey ?? '' : '',
  );
  protected readonly harvestReadonlyActionReasonKey = computed(() =>
    this.isHarvestDateReadonlyAction() ? this.resolvedHarvestDateConfig().disabledReasonKey ?? '' : '',
  );

  constructor() {
    effect(() => {
      if (this.isPlantDateInputDisabled()) {
        this.plantingDateFormControl.disable({ emitEvent: false });
      } else {
        this.plantingDateFormControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      if (this.isEmergenceDateDisabled()) {
        this.emergenceDateFormControl.disable({ emitEvent: false });
      } else {
        this.emergenceDateFormControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      if (this.isHarvestDateDisabled()) {
        this.harvestDateFormControl.disable({ emitEvent: false });
      } else {
        this.harvestDateFormControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const selectedCropField = this.selectedCropField();
      if (selectedCropField != null) {
        this.adviceDateMin = selectedCropField.plantDate;
        const plantDateValue = this.resolvedPlantDateConfig().value === undefined
          ? selectedCropField.plantDate
          : this.resolvedPlantDateConfig().value;
        const emergenceDateValue = this.resolvedEmergenceDateConfig().value === undefined
          ? selectedCropField.emergenceDate
          : this.resolvedEmergenceDateConfig().value;
        const harvestDateValue = this.resolvedHarvestDateConfig().value === undefined
          ? selectedCropField.harvestDate
          : this.resolvedHarvestDateConfig().value;

        this.plantingDateFormControl.setValue(plantDateValue ?? null, { emitEvent: false });
        this.emergenceDateFormControl.setValue(emergenceDateValue ?? null, { emitEvent: false });
        this.harvestDateFormControl.setValue(harvestDateValue ?? null, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToPlantingDateChanged();
    this.subscribeToEmergenceDateChanged();
    this.subscribeToHarvestDateChanged();
  }

  private subscribeToPlantingDateChanged(): void {
    this.plantingDateFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe((newDate) => {
      const plantingDate = toUtcDate(newDate);
      if (!plantingDate || !this.plantingDateFormControl.valid || isSameDate(plantingDate, this.selectedCropField().plantDate)) {
        return;
      }

      this.plantDateChanged.emit(plantingDate);
      this.plantDateChangedInFuture.emit(plantingDate.valueOf() > Date.now());
    });
  }

  private subscribeToEmergenceDateChanged(): void {
    this.emergenceDateFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newDate) => {
      const emergenceDate = toUtcDate(newDate);
      if (!emergenceDate || !this.emergenceDateFormControl.valid || isSameDate(emergenceDate, this.selectedCropField().emergenceDate)) {
        return;
      }

      this.emergenceDateChanged.emit(emergenceDate);
    });
  }

  private subscribeToHarvestDateChanged(): void {
    this.harvestDateFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newDate) => {
      const harvestDate = toUtcDate(newDate);
      if (!harvestDate || !this.harvestDateFormControl.valid || isSameDate(harvestDate, this.selectedCropField().harvestDate)) {
        return;
      }

      this.harvestDateChanged.emit(harvestDate);
    });
  }

  protected onEmergenceDateInteraction(event: Event, picker: MatDatepicker<Date>): void {
    if (this.isEmergenceDateDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isReadonlyActionDisabled('emergence')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isEmergenceDateReadonlyAction()) {
      event.preventDefault();
      event.stopPropagation();
      if (event instanceof KeyboardEvent) {
        this.readonlyDateActionTriggered.emit('emergence');
      }
      return;
    }

    picker.open();
  }

  protected onHarvestDateInteraction(event: Event, picker: MatDatepicker<Date>): void {
    if (this.isHarvestDateDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isReadonlyActionDisabled('harvest')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isHarvestDateReadonlyAction()) {
      event.preventDefault();
      event.stopPropagation();
      if (event instanceof KeyboardEvent) {
        this.readonlyDateActionTriggered.emit('harvest');
      }
      return;
    }

    picker.open();
  }

  protected onReadonlyDateActionClicked(event: MouseEvent, field: 'emergence' | 'harvest'): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isReadonlyActionDisabled(field)) {
      return;
    }

    this.readonlyDateActionTriggered.emit(field);
  }

  protected onReadonlyDateMouseDown(event: MouseEvent, field: 'emergence' | 'harvest'): void {
    const isReadonly = field === 'emergence' ? this.isEmergenceDateReadonlyAction() : this.isHarvestDateReadonlyAction();
    if (!isReadonly || this.isReadonlyActionDisabled(field)) {
      return;
    }

    const target = event.target;
    if (target instanceof HTMLElement && target.closest('button')) {
      return;
    }

    // Block focus/selection transitions and trigger the readonly action once for pointer interaction.
    event.preventDefault();
    event.stopPropagation();
    this.readonlyDateActionTriggered.emit(field);
  }

  private resolveDateFieldConfig(config: DateFieldConfig, defaultLabelKey: string): Required<Pick<DateFieldConfig, 'labelKey' | 'mode'>> & DateFieldConfig {
    return {
      labelKey: config.labelKey || defaultLabelKey,
      mode: config.mode ?? 'editable',
      value: config.value,
      disabledReasonKey: config.disabledReasonKey,
    };
  }

  protected isReadonlyActionDisabled(field: DateFieldName): boolean {
    return field === 'emergence'
      ? this.emergenceReadonlyActionReasonKey().length > 0
      : this.harvestReadonlyActionReasonKey().length > 0;
  }
}

const isSameDate = (left?: Date, right?: Date): boolean => {
  if (!left || !right) {
    return false;
  }

  return left.getTime() === right.getTime();
};
